import * as React from 'react';
import { FunctionComponent, useCallback, useContext, useMemo, useState } from 'react';
import { IStackTokens, Stack } from '@fluentui/react/lib/Stack';
import { DocumentCard, getTheme, SearchBox } from '@fluentui/react';
import { flatCategoryTraits, IFlatTraitDef } from '../../services';
import Highlighter from 'react-highlight-words';
import { ReviewContext } from './ReviewContextProvider';

const stackTokens: Partial<IStackTokens> = { childrenGap: 20 };

const searchFunction = (traits: IFlatTraitDef[], searchString: string): IFlatTraitDef[] => {
  const filteredTraits: IFlatTraitDef[] = [];
  traits.forEach(trait => {
    const fullString = trait.trait.concat(' ' + trait.category, trait.definition, trait.examples.join(' '));
    // This regex checks for each word and is case-insensitive.
    // Allowing for multiple words to be searched for independently
    if (RegExp(createRegx(searchString)).exec(fullString.toLowerCase())) {
      filteredTraits.push(trait);
    }
  });
  return filteredTraits;
};

const createRegx = (searchString: string): string => {
  return (
    searchString
      .toLowerCase()
      .split(' ')
      .filter(empty => empty && empty !== ' ')
      .map(word => `(?=.*${word})`)
      .join('') + '.*'
  );
};

export const TraitLookup: FunctionComponent = () => {
  const theme = getTheme();
  const themeColor = theme.palette.themeSecondary;
  const Label = props => (
    <div
      style={{
        display: 'flex',
        fontWeight: 600,
        fontSize: '10px',
        alignItems: 'center',
        justifyContent: 'flex-start',
        color: themeColor
      }}
    >
      <div style={{ border: `1px solid ${themeColor}`, width: '3%' }} />
      <span style={{ padding: '0 0px' }}>{props.children}</span>
      <div style={{ border: `1px solid ${themeColor}`, width: '90%' }} />
    </div>
  );

  const { state } = useContext(ReviewContext);
  const queueData = state?.queue?.categorizedTraitDefinitions;
  const allTraits = (queueData && flatCategoryTraits(queueData)) ?? [];

  const [traitData, setTraitData] = useState<IFlatTraitDef[]>(allTraits);
  const [searchWord, setSearchWord] = useState<string>('');
  const getHighLightWords = useMemo(() => searchWord.split(' '), [searchWord]);
  const createHighLight = useCallback(
    (text: string) => (
      <div style={{ padding: '0px 10px ' }}>
        <Highlighter
          highlightClassName="YourHighlightClass"
          searchWords={getHighLightWords}
          autoEscape={true}
          textToHighlight={text}
          highlightStyle={{ fontWeight: 600, background: theme.palette.themeLight }}
        />
      </div>
    ),
    [getHighLightWords]
  );

  const createItemsList = useCallback(
    (title: string, traitList: string[]) => (
      <>
        <Label>{title}</Label>
        <div style={{ padding: '0px 10px' }}>
          {traitList.map(traitItem => (
            <div key={traitItem}>
              -{' '}
              <Highlighter
                highlightClassName="YourHighlightClass"
                searchWords={getHighLightWords}
                autoEscape={true}
                textToHighlight={traitItem}
                highlightStyle={{ fontWeight: 600, background: theme.palette.themeLight }}
              />
            </div>
          ))}
        </div>
      </>
    ),
    [getHighLightWords]
  );

  const createCards = useMemo(
    () =>
      traitData.map(trait => (
        <DocumentCard key={trait.trait} style={{ borderColor: theme.palette.neutralTertiary }}>
          <div style={{ fontSize: 18, paddingTop: '5px', fontWeight: 800 }}>{createHighLight(trait.trait)}</div>
          <div>
            <Label>Category</Label>
            {createHighLight(trait.category)}
            <Label>Definition</Label>
            {createHighLight(trait.definition + '.')}
            {createItemsList('Examples', trait.examples)}
            {trait.exemptions && createItemsList('Exceptions', trait.exemptions)}
          </div>
        </DocumentCard>
      )),
    [traitData]
  );

  return (
    <Stack tokens={stackTokens}>
      <SearchBox
        placeholder="Search"
        onChange={(e, val) => {
          if (val) {
            setTraitData(searchFunction(allTraits, val));
          } else {
            setTraitData(allTraits);
          }
          setSearchWord(val ?? ' ');
        }}
      />
      {createCards}
    </Stack>
  );
};
